/**
 * Mapping Service
 * Contains all mapping/unmapping operations
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../../../services/useApi';
import {
  IApiResponse,
  IMapUnitsRequest,
  IMapUnitsResponse,
  IUnmapUnitsRequest,
  IUnmapUnitsResponse,
  IMsoolUnit,
  IGetMsoolUnitsResponse,
} from '../types';

// ============================================
// API Endpoints
// ============================================

const API_ENDPOINTS = {
  MAP_UNITS: '/api/Mapping/Mapping',
  UNMAP_UNITS: '/api/Mapping/UnMapping',
  GET_MSOOL_UNITS: '/api/Mapping/GetMappedProperties',
} as const;

// ============================================
// Query Hooks
// ============================================

/**
 * Hook to fetch Msool mapped units
 * @returns Query result with Msool units data
 */
export const useGetMsoolUnits = () => {
  const { apiPrivate } = useApi();

  const fetchMsoolUnits = async (): Promise<IGetMsoolUnitsResponse> => {
    const response = await apiPrivate.get<IApiResponse<IMsoolUnit[]>>(
      API_ENDPOINTS.GET_MSOOL_UNITS
    );

    if (!response.data.isSuccess) {
      throw new Error(
        response.data.errorMessages?.join(', ') || 'Failed to fetch Msool units'
      );
    }

    // Defensive handling for API response structure
    const apiUnits = response.data.result || [];
    
    // Normalize units - handle different possible field names from API
    const units: IMsoolUnit[] = apiUnits.map((unit) => {
      // Handle case where API might return 'linkedUnits' or 'mappedChannels' instead of 'channels'
      const channels = unit.channels || 
        (unit as { linkedUnits?: typeof unit.channels }).linkedUnits || 
        (unit as { mappedChannels?: typeof unit.channels }).mappedChannels || 
        [];
      
      return {
        ...unit,
        channels: Array.isArray(channels) ? channels : [],
      };
    });

    return {
      units,
    };
  };

  return useQuery({
    queryKey: ['msool-units'],
    queryFn: fetchMsoolUnits,
    staleTime: 0,
    retry: 2,
  });
};

// ============================================
// Mutation Hooks
// ============================================

/**
 * Hook to map/merge units from different channels into a Msool unit
 * @returns Mutation for mapping units
 */
export const useMapUnits = () => {
  const { apiPrivate } = useApi();
  const queryClient = useQueryClient();

  const mapUnits = async (request: IMapUnitsRequest): Promise<IMapUnitsResponse> => {
    const response = await apiPrivate.post<IApiResponse<IMapUnitsResponse>>(
      API_ENDPOINTS.MAP_UNITS,
      request
    );

    if (!response.data.isSuccess) {
      const error = new Error(
        response.data.errorMessages?.join(', ') || 'Failed to map units'
      ) as Error & { messageAr?: string };
      error.messageAr = 'فشل في دمج الوحدات';
      throw error;
    }

    return response.data.result;
  };

  return useMutation({
    mutationFn: mapUnits,
    onSuccess: () => {
      // Invalidate all related queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['msool-units'] });
      queryClient.invalidateQueries({ queryKey: ['airbnb-units'] });
      queryClient.invalidateQueries({ queryKey: ['booking-units'] });
      queryClient.invalidateQueries({ queryKey: ['gathern-units'] });
    },
  });
};

/**
 * Hook to unmap/separate units from a Msool unit
 * @returns Mutation for unmapping units
 */
export const useUnmapUnits = () => {
  const { apiPrivate } = useApi();
  const queryClient = useQueryClient();

  const unmapUnits = async (request: IUnmapUnitsRequest): Promise<IUnmapUnitsResponse> => {
    const response = await apiPrivate.post<IApiResponse<IUnmapUnitsResponse>>(
      API_ENDPOINTS.UNMAP_UNITS,
      request
    );

    if (!response.data.isSuccess) {
      const error = new Error(
        response.data.errorMessages?.join(', ') || 'Failed to unmap units'
      ) as Error & { messageAr?: string };
      error.messageAr = 'فشل في فك دمج الوحدات';
      throw error;
    }

    return response.data.result;
  };

  return useMutation({
    mutationFn: unmapUnits,
    onSuccess: () => {
      // Invalidate all related queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['msool-units'] });
      queryClient.invalidateQueries({ queryKey: ['airbnb-units'] });
      queryClient.invalidateQueries({ queryKey: ['booking-units'] });
      queryClient.invalidateQueries({ queryKey: ['gathern-units'] });
    },
  });
};
